import {
  EventSubType,
  EventType,
  EventSubTypeDetail,
  EventTypeDetail,
  NewEventSubType,
  NewEventType,
  UpdateEventSubType,
  UpdateEventType,
} from "../../models/events";
import { api } from "./api";
import { mapEventType, mapEventSubType, mapEventTypeDetail, mapEventSubTypeDetail } from "../../utils/mappers";

const eventTypeTag = (id: number) => ({ type: "eventTypes" as const, id });
const eventTypeListTag = { type: "eventTypes" as const, id: "LIST" };
const eventSubTypeTag = (id: number) => ({ type: "eventSubTypes" as const, id });
const eventSubTypeListTag = { type: "eventSubTypes" as const, id: "LIST" };

const taxonomyInvalidationTags = [eventTypeListTag, eventSubTypeListTag];

export interface EventTypeCatalogQuery {
  tenantId?: string;
}

export interface EventTaxonomyResetResult {
  resetCount: number;
}

const eventTypeCatalogQueryParams = (params: EventTypeCatalogQuery | void) =>
  params?.tenantId ? { params: { tenantId: params.tenantId } } : {};

export interface EventTypeAdminListQuery {
  page: number;
  limit: number;
  sortBy: "name" | "id";
  sortOrder: "asc" | "desc";
  tenantId?: string;
  search?: string;
}

export interface EventSubTypeAdminListQuery {
  page: number;
  limit: number;
  sortBy: "name" | "createDate" | "id";
  sortOrder: "asc" | "desc";
  eventTypeId?: number;
  tenantId?: string;
  search?: string;
}

export interface EventTypeAdminListResult {
  items: EventType[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasOwnCatalog: boolean;
}

export interface EventSubTypeAdminListResult {
  items: EventSubType[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasOwnCatalog: boolean;
}

type PagedResponse = {
  success?: boolean;
  data?: unknown[];
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
    hasOwnCatalog?: boolean;
  };
};

const emptyPagedResult = {
  items: [] as never[],
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0,
  hasOwnCatalog: false,
};

const unwrapPagedList = <T>(
  response: PagedResponse | unknown,
  mapItem: (row: unknown) => T
): {
  items: T[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasOwnCatalog: boolean;
} => {
  if (!response || typeof response !== "object") {
    return { ...emptyPagedResult, items: [] };
  }

  const record = response as PagedResponse;
  const rows = record.success && Array.isArray(record.data) ? record.data : [];
  const meta = record.meta ?? {};

  return {
    items: rows.map(mapItem),
    page: Number(meta.page ?? 1) || 1,
    limit: Number(meta.limit ?? 10) || 10,
    total: Number(meta.total ?? 0) || 0,
    totalPages: Number(meta.totalPages ?? 0) || 0,
    hasOwnCatalog: meta.hasOwnCatalog === true,
  };
};

const unwrapCatalog = (response: unknown): unknown[] => {
  if (Array.isArray(response)) {
    return response;
  }

  if (response && typeof response === "object") {
    const record = response as { success?: boolean; data?: unknown };
    if (record.success && Array.isArray(record.data)) {
      return record.data;
    }
  }

  return [];
};

const unwrapMutationData = (response: unknown): unknown => {
  if (response && typeof response === "object") {
    const record = response as { success?: boolean; data?: unknown };
    if (record.success && record.data !== undefined) {
      return record.data;
    }
  }

  return response;
};

export const eventsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getEventTypes: build.query<EventType[], EventTypeCatalogQuery | void>({
      query: (params) => ({
        url: "/bo/events/types",
        ...eventTypeCatalogQueryParams(params),
      }),
      transformResponse: (response: unknown) => unwrapCatalog(response).map(mapEventType),
      providesTags: (result) =>
        result
          ? [...result.map((item) => eventTypeTag(item.eventTypeId)), eventTypeListTag]
          : [eventTypeListTag],
    }),
    getEventSubTypes: build.query<EventSubType[], EventTypeCatalogQuery | void>({
      query: (params) => ({
        url: "/bo/events/types/sub-types",
        ...eventTypeCatalogQueryParams(params),
      }),
      transformResponse: (response: unknown) => unwrapCatalog(response).map(mapEventSubType),
      providesTags: (result) =>
        result
          ? [...result.map((item) => eventSubTypeTag(item.eventSubTypeId)), eventSubTypeListTag]
          : [eventSubTypeListTag],
    }),
    getEventTypesPaged: build.query<EventTypeAdminListResult, EventTypeAdminListQuery>({
      query: (params) => ({
        url: "/bo/events/event-types",
        params,
      }),
      transformResponse: (response: unknown) => unwrapPagedList(response, mapEventType),
      providesTags: (result) =>
        result
          ? [...result.items.map((item) => eventTypeTag(item.eventTypeId)), eventTypeListTag]
          : [eventTypeListTag],
    }),
    getEventSubTypesPaged: build.query<EventSubTypeAdminListResult, EventSubTypeAdminListQuery>({
      query: ({ eventTypeId, ...params }) => ({
        url: "/bo/events/types/sub-types",
        params: {
          ...params,
          ...(eventTypeId !== undefined ? { eventTypeId } : {}),
        },
      }),
      transformResponse: (response: unknown) => unwrapPagedList(response, mapEventSubType),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((item) => eventSubTypeTag(item.eventSubTypeId)),
              eventSubTypeListTag,
            ]
          : [eventSubTypeListTag],
    }),
    getEventTypeDetail: build.query<EventTypeDetail, { id: number; tenantId?: string }>({
      query: ({ id, tenantId }) => ({
        url: `/bo/events/event-types/${id}`,
        params: tenantId ? { tenantId } : {},
      }),
      transformResponse: (response: unknown) => mapEventTypeDetail(unwrapMutationData(response)),
      providesTags: (_result, _error, { id }) => [eventTypeTag(id)],
    }),
    getEventSubTypeDetail: build.query<EventSubTypeDetail, { id: number; tenantId?: string }>({
      query: ({ id, tenantId }) => ({
        url: `/bo/events/types/${id}`,
        params: tenantId ? { tenantId } : {},
      }),
      transformResponse: (response: unknown) => mapEventSubTypeDetail(unwrapMutationData(response)),
      providesTags: (_result, _error, { id }) => [eventSubTypeTag(id)],
    }),
    createEventType: build.mutation<EventType, NewEventType>({
      query: (body) => ({
        url: "/bo/events/event-types",
        method: "POST",
        body,
      }),
      transformResponse: (response: unknown) => mapEventType(unwrapMutationData(response)),
      invalidatesTags: taxonomyInvalidationTags,
    }),
    updateEventType: build.mutation<EventType, UpdateEventType>({
      query: ({ id, name, tenantId }) => ({
        url: `/bo/events/event-types/${id}`,
        method: "PATCH",
        body: { name, ...(tenantId !== undefined ? { tenantId } : {}) },
      }),
      transformResponse: (response: unknown) => mapEventType(unwrapMutationData(response)),
      invalidatesTags: (_result, _error, { id }) => [
        eventTypeTag(id),
        ...taxonomyInvalidationTags,
      ],
    }),
    deleteEventType: build.mutation<void, number>({
      query: (id) => ({
        url: `/bo/events/event-types/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [eventTypeTag(id), ...taxonomyInvalidationTags],
    }),
    newEventSubType: build.mutation<void, NewEventSubType>({
      query: (data) => ({
        url: "/bo/events/types",
        method: "POST",
        body: data,
      }),
      invalidatesTags: taxonomyInvalidationTags,
    }),
    updateEventSubType: build.mutation<void, UpdateEventSubType>({
      query: (data) => ({
        url: "/bo/events/types",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { eventSubTypeId }) => [
        eventSubTypeTag(eventSubTypeId),
        ...taxonomyInvalidationTags,
      ],
    }),
    deleteEventSubType: build.mutation<void, string | number>({
      query: (id) => ({
        url: `/bo/events/types/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        eventSubTypeTag(Number(id)),
        ...taxonomyInvalidationTags,
      ],
    }),
    resetEventTaxonomy: build.mutation<EventTaxonomyResetResult, void>({
      query: () => ({
        url: "/bo/events/event-types/reset",
        method: "POST",
      }),
      transformResponse: (response: unknown): EventTaxonomyResetResult => {
        const data = unwrapMutationData(response) as { resetCount?: unknown } | null;
        return { resetCount: Number(data?.resetCount ?? 0) || 0 };
      },
      invalidatesTags: taxonomyInvalidationTags,
    }),
  }),
});

export const {
  useGetEventTypesQuery,
  useGetEventSubTypesQuery,
  useGetEventTypesPagedQuery,
  useGetEventSubTypesPagedQuery,
  useGetEventTypeDetailQuery,
  useGetEventSubTypeDetailQuery,
  useCreateEventTypeMutation,
  useUpdateEventTypeMutation,
  useDeleteEventTypeMutation,
  useNewEventSubTypeMutation,
  useUpdateEventSubTypeMutation,
  useDeleteEventSubTypeMutation,
  useResetEventTaxonomyMutation,
} = eventsApi;
